import { supabase } from '../lib/supabase';

// Interfaces for reposition-related data
interface Reposition {
  id: string;
  title: string;
  description: string;
  group_id: string;
  status_id: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  total_participants?: number;
  total_cost?: number;
}

interface RepositionWithDetails extends Reposition {
  group: {
    name: string;
    code: string;
  };
  creator: {
    name: string;
    email: string;
  };
  status: {
    name: string;
    description: string;
  };
}

interface RepositionParticipant {
  id: string;
  reposition_id: string;
  order_id: string;
  individual_cost: number;
  reason: string;
  evidence_urls: string[] | null;
  created_at: string;
  status_id: number;
  status?: {
    name: string;
    color: string;
  };
  order?: {
    garment_name: string;
    color_name: string;
    size_name: string;
    user_name: string;
    user_email: string;
  };
}

interface CreateRepositionData {
  title: string;
  description: string;
  group_id: string;
}

interface AddOrderToRepositionData {
  group_id: string;
  order_id: string;
  reason: string;
  evidence_urls?: string[] | null;
}

interface RepositionResponse {
  success: boolean;
  error?: string;
  message?: string;
  data?: any;
}

interface RepositionComputedStatus {
  status_name: string;
  computed_key: 'pending' | 'ready' | 'in_production';
  all_validated: boolean;
  can_send_to_production: boolean;
}

const REPOSITION_PARTICIPANT_STATUS = {
  PENDING: 1,
  APPROVED: 2,
  DENIED: 3,
} as const;

class RepositionService {
  /**
   * Computed status from participants + whether a production order exists.
   * pending → Pendiente | ready → Listo | in_production → Completado
   */
  computeRepositionStatus(
    participants: Array<{ status_id: number }>,
    hasProductionOrder: boolean = false
  ): RepositionComputedStatus {
    const total = participants.length;
    const pending = participants.filter(
      (p) => p.status_id === REPOSITION_PARTICIPANT_STATUS.PENDING
    ).length;
    const validated = participants.filter(
      (p) => p.status_id === REPOSITION_PARTICIPANT_STATUS.APPROVED
    ).length;

    if (total === 0 || pending > 0 || validated === 0) {
      return {
        status_name: 'Pendiente',
        computed_key: 'pending',
        all_validated: false,
        can_send_to_production: false,
      };
    }

    if (hasProductionOrder) {
      return {
        status_name: 'Completado',
        computed_key: 'in_production',
        all_validated: true,
        can_send_to_production: false,
      };
    }

    return {
      status_name: 'Listo',
      computed_key: 'ready',
      all_validated: true,
      can_send_to_production: true,
    };
  }

  /**
   * Whether a production order exists for this reposition.
   * Uses a security-definer RPC so anon does not need SELECT on production_orders.
   */
  async hasProductionOrderForReposition(repositionId: string): Promise<boolean> {
    if (!repositionId) return false;

    const { data, error } = await supabase.rpc('has_production_order_for_reposition', {
      p_reposition_id: repositionId,
    });

    if (error) return false;
    return data === true;
  }

  /**
   * Get computed status for the group's active reposition (if any).
   */
  async getComputedStatusForGroup(groupId: string): Promise<RepositionComputedStatus | null> {
    try {
      const repositions = await this.getActiveRepositions(groupId);
      if (repositions.length === 0) return null;

      const repositionId = repositions[0].id;

      const [{ data: participants }, hasProductionOrder] = await Promise.all([
        supabase
          .from('reposition_participants')
          .select('status_id')
          .eq('reposition_id', repositionId),
        this.hasProductionOrderForReposition(repositionId),
      ]);

      return this.computeRepositionStatus(participants || [], hasProductionOrder);
    } catch {
      return null;
    }
  }

  /**
   * Get all active repositions for a specific group
   */
  async getActiveRepositions(groupId?: string): Promise<Reposition[]> {
    try {
      let query = supabase
        .from('repositions')
        .select('*')
        .is('resolved_at', null) // Active statuses (not resolved)
        .order('created_at', { ascending: false }).limit(1);

      if (groupId) {
        query = query.eq('group_id', groupId);
      }

      const { data, error } = await query;

      if (error) {
        return [];
      }

      return data || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Add an order to an existing reposition using the database function
   */
  async addOrderToReposition(data: AddOrderToRepositionData): Promise<RepositionResponse> {
    try {
      const reposition = await this.getActiveRepositions(data.group_id);
      if (reposition.length === 0) {
        return {
          success: false,
          error: 'No hay una reposición activa para este grupo'
        };
      }

      const computedStatus = await this.getComputedStatusForGroup(data.group_id);
      if (computedStatus?.computed_key === 'in_production') {
        return {
          success: false,
          error: 'Esta reposición ya está en producción y no admite nuevas solicitudes'
        };
      }

      const { data: result, error } = await supabase.rpc('add_to_reposition', {
        p_reposition_id: reposition[0].id,
        p_order_id: data.order_id,
        p_reason: data.reason,
        p_evidence_urls: data.evidence_urls
      });

      if (error) {
        return {
          success: false,
          error: 'Error al procesar la solicitud'
        };
      }

      if (result && !result.success) {
        return {
          success: false,
          error: result.error
        };
      }

      return result || {
        success: true,
        message: 'Pedido añadido a la reposición correctamente'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Error inesperado al añadir el pedido a la reposición'
      };
    }
  }

  /**
   * Check if user is already participating in a reposition with a specific order
   */
  async isUserParticipating(repositionId: string, orderId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('reposition_participants')
        .select('reposition_id')
        .eq('reposition_id', repositionId)
        .eq('order_id', orderId)
        .maybeSingle();

      if (error) {
        return false;
      }

      return !!data;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get user participation details for a specific group
   */
  async getUserParticipation(groupId: string, orderId: string): Promise<RepositionParticipant | null> {
    try {
      // 1. Get active reposition for group
      const reposition = await this.getActiveRepositions(groupId);
      if (reposition.length === 0) return null;

      const repositionId = reposition[0].id;

      // 2. Get participation details with status
      const { data, error } = await supabase
        .from('reposition_participants')
        .select(`
          *,
          status:reposition_statuses (
            name,
            color
          )
        `)
        .eq('reposition_id', repositionId)
        .eq('order_id', orderId)
        .maybeSingle();

      if (error || !data) return null;

      return data as RepositionParticipant;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update reposition status (for admins/creators)
   */
}

export const repositionService = new RepositionService();
export type { 
  Reposition, 
  RepositionWithDetails, 
  RepositionParticipant, 
  CreateRepositionData, 
  AddOrderToRepositionData, 
  RepositionResponse,
  RepositionComputedStatus,
};
